import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';

import { dayName, monthDay } from '../../helpers';
import styles from './ClinicLocalTime.module.scss';

const getBrowserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const getTimeZoneLabel = (date, time, timeZone = getBrowserTimeZone()) => {
  if (!timeZone) return '';

  const normalizedTime = `${time || ''}`.trim();
  const normalizedDate = `${date || ''}`.trim();
  const fallbackLabel = timeZone.split('/').pop()?.replace(/_/g, ' ') || timeZone;

  if (!normalizedDate) {
    return fallbackLabel;
  }

  const zonedDateTime = moment.tz(
    `${normalizedDate} ${normalizedTime || '00:00'}`,
    ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DD'],
    timeZone,
  );

  if (zonedDateTime.isValid()) {
    const abbreviation = zonedDateTime.format('z');
    if (abbreviation && !abbreviation.startsWith('+') && abbreviation !== 'UTC') {
      return abbreviation;
    }
  }

  return fallbackLabel;
};

const ClinicLocalTime = ({ appt, clinicName, compact = false, className }) => {
  const clinicTime = `${appt?.departmentLocalTime || ''}`.trim();
  const clinicDate = `${appt?.departmentLocalDate || ''}`.trim();
  const localDate = `${appt?.date || ''}`.trim();
  const clinicZone = getTimeZoneLabel(
    clinicDate || localDate,
    clinicTime,
    appt?.departmentTimezone,
  );
  const localZone = getTimeZoneLabel(localDate, appt?.time);
  const localDay =
    clinicDate && localDate && clinicDate !== localDate
      ? `${dayName(localDate)} ${monthDay(localDate)}`
      : '';

  if (compact) {
    return (
      <div className={[styles.compact, className].filter(Boolean).join(' ')}>
        {clinicTime ? (
          <span>
            <b>Clinic</b> {`${clinicTime} ${clinicZone}`.trim()}
          </span>
        ) : null}
        <span>
          <b>Local</b> {`${appt?.time || ''} ${localZone}`.trim()}
          {localDay ? ` (${localDay})` : ''}
        </span>
      </div>
    );
  }

  return (
    <dl className={[styles.clinicLocal, className].filter(Boolean).join(' ')}>
      {clinicTime ? (
        <>
          <dt>{clinicName || 'Clinic'}</dt>
          <dd>{`${clinicTime} ${clinicZone}`.trim()}</dd>
        </>
      ) : null}
      <dt className={styles.localLabel}>Local</dt>
      <dd className={styles.localTime}>
        <span>{`${appt?.time || ''} ${localZone}`.trim()}</span>
        {localDay ? <span>{` (${localDay})`}</span> : null}
      </dd>
    </dl>
  );
};

ClinicLocalTime.propTypes = {
  appt: PropTypes.object,
  clinicName: PropTypes.string,
  compact: PropTypes.bool,
  className: PropTypes.string,
};

export default ClinicLocalTime;
